import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../hooks/useAuth';
import { Icons } from '../components/Icons';
import * as XLSX from 'xlsx';

function ContainerPrePurchaseList() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    no: '',
    date: '',
    name: '',
    priceExcludingTax: '',
    priceIncludingTax: '',
    phoneNumber: '',
    item: '',
    designer: '',
    manufacturer: '',
    quantity: 1,
    condition: '',
    location: '',
    restorationHistory: '',
    releaseSchedule: '',
    memo: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0 });

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const itemsRef = collection(db, 'container-pre-purchases');
      const q = query(itemsRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);

      const itemList = snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      }));

      setItems(itemList);
    } catch (error) {
      console.error('컨테이너 선구매 리스트 불러오기 오류:', error);
      alert('컨테이너 선구매 리스트를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = async () => {
    if (!formData.item.trim()) {
      alert('제품명(Item)을 입력하세요.');
      return;
    }

    try {
      await addDoc(collection(db, 'container-pre-purchases'), {
        ...formData,
        quantity: Number(formData.quantity) || 1,
        priceExcludingTax: Number(formData.priceExcludingTax) || 0,
        priceIncludingTax: Number(formData.priceIncludingTax) || 0,
        createdAt: serverTimestamp(),
        createdBy: user.email
      });

      alert('✅ 컨테이너 선구매 항목이 추가되었습니다.');
      setShowAddModal(false);
      resetForm();
      fetchItems();
    } catch (error) {
      console.error('컨테이너 선구매 항목 추가 오류:', error);
      alert('컨테이너 선구매 항목 추가 중 오류가 발생했습니다.');
    }
  };

  const handleUpdateItem = async () => {
    if (!formData.item.trim()) {
      alert('제품명(Item)을 입력하세요.');
      return;
    }

    try {
      const itemRef = doc(db, 'container-pre-purchases', editingItem.id);
      await updateDoc(itemRef, {
        ...formData,
        quantity: Number(formData.quantity) || 1,
        priceExcludingTax: Number(formData.priceExcludingTax) || 0,
        priceIncludingTax: Number(formData.priceIncludingTax) || 0,
        updatedAt: serverTimestamp(),
        updatedBy: user.email
      });

      alert('✅ 컨테이너 선구매 항목이 수정되었습니다.');
      setEditingItem(null);
      resetForm();
      fetchItems();
    } catch (error) {
      console.error('컨테이너 선구매 항목 수정 오류:', error);
      alert('컨테이너 선구매 항목 수정 중 오류가 발생했습니다.');
    }
  };

  const handleDeleteItem = async (itemId, itemName) => {
    if (!window.confirm(`"${itemName}" 항목을 삭제하시겠습니까?`)) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'container-pre-purchases', itemId));
      alert('✅ 컨테이너 선구매 항목이 삭제되었습니다.');
      fetchItems();
    } catch (error) {
      console.error('컨테이너 선구매 항목 삭제 오류:', error);
      alert('컨테이너 선구매 항목 삭제 중 오류가 발생했습니다.');
    }
  };

  const startEdit = (item) => {
    setEditingItem(item);
    setFormData({
      no: item.no || '',
      date: item.date || '',
      name: item.name || '',
      priceExcludingTax: item.priceExcludingTax || '',
      priceIncludingTax: item.priceIncludingTax || '',
      phoneNumber: item.phoneNumber || '',
      item: item.item || '',
      designer: item.designer || '',
      manufacturer: item.manufacturer || '',
      quantity: item.quantity || 1,
      condition: item.condition || '',
      location: item.location || '',
      restorationHistory: item.restorationHistory || '',
      releaseSchedule: item.releaseSchedule || '',
      memo: item.memo || ''
    });
  };

  const resetForm = () => {
    setFormData({
      no: '',
      date: '',
      name: '',
      priceExcludingTax: '',
      priceIncludingTax: '',
      phoneNumber: '',
      item: '',
      designer: '',
      manufacturer: '',
      quantity: 1,
      condition: '',
      location: '',
      restorationHistory: '',
      releaseSchedule: '',
      memo: ''
    });
  };

  const closeModal = () => {
    setShowAddModal(false);
    setEditingItem(null);
    resetForm();
  };

  // 유연한 컬럼 값 추출 함수
  const getColumnValue = (row, possibleNames) => {
    for (const name of possibleNames) {
      if (row[name] !== undefined && row[name] !== null && row[name] !== '') {
        return row[name];
      }
    }
    return null;
  };

  // 엑셀 임포트
  const handleImportExcel = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImporting(true);
    setImportProgress({ current: 0, total: 0 });

    try {
      const reader = new FileReader();
      reader.onload = async (evt) => {
        try {
          const data = evt.target.result;
          const workbook = XLSX.read(data, { type: 'binary' });

          // "컨테이너 선구매" 관련 시트 찾기 또는 첫 번째 시트 사용
          let sheetName = workbook.SheetNames.find(name =>
            name.includes('컨테이너') || name.includes('선구매 제품')
          );
          if (!sheetName) sheetName = workbook.SheetNames[0];

          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

          let successCount = 0;
          let errorCount = 0;
          const errors = [];

          // 헤더 행 필터링
          const filteredData = jsonData.filter(row => {
            const item = getColumnValue(row, ['Item', 'item', '제품', '제품명', '상품명']);
            return item &&
                   item !== 'Item' &&
                   item !== '제품' &&
                   item !== '제품명' &&
                   !String(item).includes('컨테이너 선구매');
          });

          setImportProgress({ current: 0, total: filteredData.length });

          for (let i = 0; i < filteredData.length; i++) {
            const row = filteredData[i];

            try {
              const item = getColumnValue(row, ['Item', 'item', '제품', '제품명', '상품명']);

              if (!item) {
                errors.push(`행 ${i + 2}: 제품명(Item)이 누락되었습니다.`);
                errorCount++;
                continue;
              }

              const no = getColumnValue(row, ['No', 'NO', 'no', '번호']);
              const date = getColumnValue(row, ['Date', 'date', '날짜', '일자']);
              const name = getColumnValue(row, ['Name', 'name', '이름', '구매자', '고객명']);
              const priceExcludingTax = getColumnValue(row, ['부가세 미포함', '부가세미포함', '공급가액', '공급가', 'Price']);
              const priceIncludingTax = getColumnValue(row, ['부가세 포함 (10%)', '부가세 포함', '부가세포함', '합계', '총액']);
              const phoneNumber = getColumnValue(row, ['Phone Number', 'Phone', 'phone', '전화번호', '연락처']);
              const designer = getColumnValue(row, ['Designer', 'designer', '디자이너']);
              const manufacturer = getColumnValue(row, ['Manufacturer', 'manufacturer', '제조사', '브랜드']);
              const quantity = getColumnValue(row, ['수량', 'Quantity', 'quantity', '개수']);
              const condition = getColumnValue(row, ['컨디션', 'Condition', 'condition', '상태']);
              const location = getColumnValue(row, ['위치', 'Location', 'location', '창고']);
              const restorationHistory = getColumnValue(row, ['복원 내역', '복원내역', '복원', 'Restoration']);
              const releaseSchedule = getColumnValue(row, ['출고 일정', '출고일정', '출고일', '출고', 'Release']);
              const memo = getColumnValue(row, ['Memo', 'memo', '메모', '비고', '특이사항']);

              await addDoc(collection(db, 'container-pre-purchases'), {
                no: no ? String(no).trim() : '',
                date: date ? String(date).trim() : '',
                name: name ? String(name).trim() : '',
                priceExcludingTax: parseInt(String(priceExcludingTax).replace(/[^0-9]/g, '')) || 0,
                priceIncludingTax: parseInt(String(priceIncludingTax).replace(/[^0-9]/g, '')) || 0,
                phoneNumber: phoneNumber ? String(phoneNumber).trim() : '',
                item: String(item).trim(),
                designer: designer ? String(designer).trim() : '',
                manufacturer: manufacturer ? String(manufacturer).trim() : '',
                quantity: parseInt(quantity) || 1,
                condition: condition ? String(condition).trim() : '',
                location: location ? String(location).trim() : '',
                restorationHistory: restorationHistory ? String(restorationHistory).trim() : '',
                releaseSchedule: releaseSchedule ? String(releaseSchedule).trim() : '',
                memo: memo ? String(memo).trim() : '',
                createdAt: serverTimestamp(),
                createdBy: user.email,
                importedAt: serverTimestamp()
              });

              successCount++;
              setImportProgress({ current: i + 1, total: filteredData.length });

              // 배치마다 잠깐 대기
              if ((i + 1) % 20 === 0) {
                await new Promise(resolve => setTimeout(resolve, 100));
              }

            } catch (error) {
              errors.push(`행 ${i + 2}: ${error.message}`);
              errorCount++;
            }
          }

          if (successCount > 0) {
            alert(`✅ ${successCount}건의 데이터가 성공적으로 가져와졌습니다!${errorCount > 0 ? `\n❌ ${errorCount}건 실패` : ''}`);
            fetchItems();
          } else if (errorCount > 0) {
            alert(`❌ 가져오기 실패: ${errors.slice(0, 5).join('\n')}`);
          } else {
            alert('가져올 데이터가 없습니다.');
          }

        } catch (error) {
          console.error('파일 파싱 오류:', error);
          alert('엑셀 파일 파싱 중 오류가 발생했습니다.');
        } finally {
          setImporting(false);
          setImportProgress({ current: 0, total: 0 });
        }
      };
      reader.readAsBinaryString(file);

    } catch (error) {
      console.error('엑셀 가져오기 오류:', error);
      alert('엑셀 가져오기 중 오류가 발생했습니다.');
      setImporting(false);
    }

    e.target.value = '';
  };

  const filteredItems = items.filter(item =>
    item.item?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.designer?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.manufacturer?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalQuantity = filteredItems.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
  const totalValue = filteredItems.reduce((sum, item) => sum + (Number(item.priceIncludingTax) || 0), 0);

  const formatCurrency = (value) => {
    if (!value) return '-';
    return new Intl.NumberFormat('ko-KR').format(value) + '원';
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <h2 className="text-3xl font-bold text-brand-900">컨테이너 선구매 제품</h2>
          <p className="text-gray-500 mt-1">컨테이너 입고 예정 선구매 제품을 관리합니다.</p>
        </div>
        <div className="flex gap-3">
          <label className={`px-4 py-2.5 bg-white hover:bg-gray-50 border border-gray-300 text-gray-700 rounded-lg transition font-medium flex items-center cursor-pointer ${importing ? 'opacity-50 cursor-not-allowed' : ''}`}>
            <Icons.Upload className="w-5 h-5 mr-2" />
            엑셀 임포트
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleImportExcel}
              disabled={importing}
              className="hidden"
            />
          </label>
          <button
            onClick={() => setShowAddModal(true)}
            className="px-6 py-3 bg-brand-600 hover:bg-brand-700 text-white rounded-lg transition font-bold flex items-center shadow-md"
          >
            <Icons.Plus className="w-5 h-5 mr-2" />
            제품 추가
          </button>
        </div>
      </div>

      {/* 임포트 진행 상태 */}
      {importing && (
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="flex items-center mb-2">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-3"></div>
            <span className="font-bold text-blue-900">엑셀 데이터를 가져오는 중...</span>
          </div>
          {importProgress.total > 0 && (
            <div className="mt-2">
              <div className="flex justify-between text-sm text-blue-700 mb-1">
                <span>진행률</span>
                <span>{importProgress.current} / {importProgress.total}</span>
              </div>
              <div className="w-full bg-blue-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all"
                  style={{ width: `${(importProgress.current / importProgress.total) * 100}%` }}
                ></div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 통계 카드 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-brand-100 p-6">
          <div className="flex items-center">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Icons.List className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-500">총 항목</p>
              <p className="text-2xl font-bold text-gray-900">{filteredItems.length}건</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-brand-100 p-6">
          <div className="flex items-center">
            <div className="p-3 bg-green-100 rounded-lg">
              <Icons.Box className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-500">총 수량</p>
              <p className="text-2xl font-bold text-gray-900">{totalQuantity}개</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-brand-100 p-6">
          <div className="flex items-center">
            <div className="p-3 bg-purple-100 rounded-lg">
              <Icons.DollarSign className="w-6 h-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-500">총 금액 (부가세 포함)</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalValue)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* 검색 */}
      <div className="mb-6 bg-white rounded-xl shadow-sm border border-brand-100 p-4">
        <div className="relative">
          <Icons.Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="제품명, 구매자, 디자이너, 제조사 검색"
            className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* 목록 */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600 mx-auto"></div>
          <p className="text-gray-500 mt-4">컨테이너 선구매 리스트를 불러오는 중...</p>
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <Icons.Truck className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-lg mb-4">
            {searchTerm ? '검색 결과가 없습니다.' : '등록된 컨테이너 선구매 항목이 없습니다.'}
          </p>
          {!searchTerm && (
            <div className="flex justify-center gap-3">
              <label className="px-6 py-3 bg-white hover:bg-gray-50 border border-gray-300 text-gray-700 rounded-lg transition font-bold cursor-pointer flex items-center">
                <Icons.Upload className="w-5 h-5 mr-2" />
                엑셀로 가져오기
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleImportExcel}
                  disabled={importing}
                  className="hidden"
                />
              </label>
              <button
                onClick={() => setShowAddModal(true)}
                className="px-6 py-3 bg-brand-600 hover:bg-brand-700 text-white rounded-lg transition font-bold"
              >
                직접 추가하기
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-brand-100 overflow-x-auto">
          <table className="w-full min-w-[1200px]">
            <thead className="bg-brand-50 border-b border-brand-100">
              <tr>
                <th className="px-4 py-4 text-left text-xs font-bold text-brand-900">No</th>
                <th className="px-4 py-4 text-left text-xs font-bold text-brand-900">날짜</th>
                <th className="px-4 py-4 text-left text-xs font-bold text-brand-900">구매자</th>
                <th className="px-4 py-4 text-left text-xs font-bold text-brand-900">제품</th>
                <th className="px-4 py-4 text-left text-xs font-bold text-brand-900">디자이너</th>
                <th className="px-4 py-4 text-right text-xs font-bold text-brand-900">부가세 미포함</th>
                <th className="px-4 py-4 text-right text-xs font-bold text-brand-900">부가세 포함</th>
                <th className="px-4 py-4 text-center text-xs font-bold text-brand-900">수량</th>
                <th className="px-4 py-4 text-left text-xs font-bold text-brand-900">컨디션</th>
                <th className="px-4 py-4 text-left text-xs font-bold text-brand-900">출고 일정</th>
                <th className="px-4 py-4 text-right text-xs font-bold text-brand-900">작업</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredItems.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50 transition">
                  <td className="px-4 py-3 text-sm text-gray-700">{item.no || '-'}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{item.date || '-'}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center">
                      <Icons.User className="w-4 h-4 text-gray-400 mr-1" />
                      <span className="text-sm text-gray-700">{item.name || '-'}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-bold text-gray-900 text-sm">{item.item}</span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">{item.designer || '-'}</td>
                  <td className="px-4 py-3 text-right text-sm text-gray-700">{formatCurrency(item.priceExcludingTax)}</td>
                  <td className="px-4 py-3 text-right text-sm font-bold text-gray-900">{formatCurrency(item.priceIncludingTax)}</td>
                  <td className="px-4 py-3 text-center">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-700">
                      {item.quantity || 1}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {item.condition && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                        {item.condition}
                      </span>
                    )}
                    {!item.condition && <span className="text-gray-400">-</span>}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">{item.releaseSchedule || '-'}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => startEdit(item)}
                        className="px-2 py-1 bg-white hover:bg-gray-50 border border-gray-300 text-gray-700 rounded-lg transition text-xs font-medium"
                      >
                        수정
                      </button>
                      <button
                        onClick={() => handleDeleteItem(item.id, item.item)}
                        className="px-2 py-1 bg-white hover:bg-red-50 border border-gray-300 hover:border-red-300 text-gray-700 hover:text-red-600 rounded-lg transition text-xs font-medium"
                      >
                        삭제
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 추가/수정 모달 */}
      {(showAddModal || editingItem) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h3 className="text-xl font-bold text-gray-900">
                {editingItem ? '컨테이너 선구매 항목 수정' : '컨테이너 선구매 항목 추가'}
              </h3>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 transition"
              >
                <Icons.X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* 기본 정보 */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">No</label>
                  <input
                    type="text"
                    value={formData.no}
                    onChange={(e) => setFormData({ ...formData, no: e.target.value })}
                    placeholder="번호"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">날짜</label>
                  <input
                    type="text"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    placeholder="예: 12월 초"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">구매자</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="구매자 이름"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* 제품 정보 */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  제품 (Item) <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.item}
                  onChange={(e) => setFormData({ ...formData, item: e.target.value })}
                  placeholder="제품명을 입력하세요"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">디자이너</label>
                  <input
                    type="text"
                    value={formData.designer}
                    onChange={(e) => setFormData({ ...formData, designer: e.target.value })}
                    placeholder="디자이너"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">제조사</label>
                  <input
                    type="text"
                    value={formData.manufacturer}
                    onChange={(e) => setFormData({ ...formData, manufacturer: e.target.value })}
                    placeholder="제조사"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* 가격 정보 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">부가세 미포함 금액</label>
                  <input
                    type="number"
                    value={formData.priceExcludingTax}
                    onChange={(e) => setFormData({ ...formData, priceExcludingTax: e.target.value })}
                    placeholder="0"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">부가세 포함 금액 (10%)</label>
                  <input
                    type="number"
                    value={formData.priceIncludingTax}
                    onChange={(e) => setFormData({ ...formData, priceIncludingTax: e.target.value })}
                    placeholder="0"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* 연락처 & 수량 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">전화번호</label>
                  <input
                    type="tel"
                    value={formData.phoneNumber}
                    onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                    placeholder="010-1234-5678"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">수량</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.quantity}
                    onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* 상태 정보 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">컨디션</label>
                  <input
                    type="text"
                    value={formData.condition}
                    onChange={(e) => setFormData({ ...formData, condition: e.target.value })}
                    placeholder="예: A급, B급"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">위치</label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    placeholder="위치"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* 추가 정보 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">복원 내역</label>
                  <input
                    type="text"
                    value={formData.restorationHistory}
                    onChange={(e) => setFormData({ ...formData, restorationHistory: e.target.value })}
                    placeholder="복원 내역"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">출고 일정</label>
                  <input
                    type="text"
                    value={formData.releaseSchedule}
                    onChange={(e) => setFormData({ ...formData, releaseSchedule: e.target.value })}
                    placeholder="예: 12월 15일"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* 메모 */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">메모</label>
                <textarea
                  value={formData.memo}
                  onChange={(e) => setFormData({ ...formData, memo: e.target.value })}
                  rows="3"
                  placeholder="메모를 입력하세요"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="sticky bottom-0 bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t border-gray-200">
              <button
                onClick={closeModal}
                className="px-6 py-3 bg-white hover:bg-gray-50 border border-gray-300 text-gray-700 rounded-lg transition font-bold"
              >
                취소
              </button>
              <button
                onClick={editingItem ? handleUpdateItem : handleAddItem}
                className="px-6 py-3 bg-brand-600 hover:bg-brand-700 text-white rounded-lg transition font-bold"
              >
                {editingItem ? '수정' : '저장'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ContainerPrePurchaseList;
